/**
 * tools/trustSignals.ts — ONE wording for every trust signal that appears on more than one surface.
 *
 * `REQUIREMENTS_TOOL_DETAIL_PAGE.md` §8 lists ten trust signals and, for each, whether a result CARD
 * shows it and whether the tool PAGE does. It also says, in as many words, that discovery and
 * Recommended Tools consume that table as the definition of what a card shows: "there is one signal
 * set in the product, and it lives here". DS-NFR06 is the consequence — a signal on a card must carry
 * the same VALUE and the same WORDING as the same signal on the tool page.
 *
 * Same value is the API's job (`db/toolSignals.ts` computes the card's numbers with the per-tool rules
 * the page uses). Same wording is this file's, and the reason it exists rather than a convention is
 * that the two surfaces are ~1200 lines apart in two different files: the card's "3 times (last 4 Aug)"
 * and the page's were written months apart and would drift the first time either was touched.
 *
 * HOW IT ENFORCES THAT, because a shared function nobody has to call enforces nothing:
 *
 *   Every formatter takes a NARROW STRUCTURAL input — `{ adoptionCount, lastAdoptedAt }`, not
 *   `CatalogueToolDetail` — which both `CatalogueToolSummary` and `CatalogueToolDetail` satisfy. So the
 *   card and the page pass the same object shape to the same function and the compiler accepts both;
 *   there is no per-surface overload to diverge, and adding a surface (Top 5, homepage strips) means
 *   calling the same function rather than copying a template string.
 *
 * WHAT IS AND IS NOT HERE, straight off §8:
 *
 *   in  — 1 adoption, 2 review marker, 3 recently confirmed working, 4 health, 5 open issues,
 *         10 rating. Every one of these is "Card: yes", so every one has two surfaces to keep together.
 *   out — 6 version + last updated, 8 estimated time saved, 9 like count. All three are "Card: no",
 *         so they appear on exactly one surface and have nothing to drift from; they stay inline in the
 *         tool page's Trust list. Moving them here would suggest a card may render them.
 *   out — 7 access requirement. It IS "Card: yes (if unmet)", but the API's access gate is a WHERE on
 *         the catalogue query: a tool whose requirement the viewer does not meet is not on the page at
 *         all. The unmet case is unreachable on a card by construction, so the page's one-line wording
 *         stays inline with it. If a future surface shows access, its formatter belongs here.
 *
 * ABSENCE IS A RETURN VALUE, not a caller's `if`. A formatter returns `null` for the signals §8 hides
 * rather than shows-as-zero (confirmed working, health when Healthy, open issues when none) and returns
 * the explicit absence SENTENCE for the two TD-F07 covers ("Not adopted yet", "Not yet reviewed"). That
 * split is the requirement, and putting it here means a new surface inherits it instead of re-deciding.
 *
 * Pure string functions with no React and no imports, deliberately: `packages/web`'s test harness runs
 * plain vitest with no DOM, so wording is only testable at all if it lives outside JSX.
 */

/**
 * The display floor for the confirmed-working COUNT — platform setting `min_confirmations_to_show_count`
 * (`0015_seed_reference_data.sql:59`, default 5). Below it, the signal says a tool works recently
 * without saying how many people said so, which is FB-F14's small-numbers rule.
 *
 * Hardcoded here rather than read from the API, and that is a known limitation, not the design: nothing
 * server-side reads that setting today, so an owner who edits it changes nothing. What this constant
 * does fix is the part that was actually broken — the number was written into the tool page's JSX, so
 * a card had no way to apply the same floor. One named value in one place is what the card and the page
 * now share; wiring it to the setting is a later change in one line.
 */
export const MIN_CONFIRMATIONS_TO_SHOW_COUNT = 5;

/**
 * The label each signal is announced by, so the card and the tool page cannot call the same signal two
 * different things — which is half of what a person notices when two surfaces disagree.
 */
export const TRUST_SIGNAL_LABELS = {
  adoption: 'Adopted',
  review: 'Reviewed',
  confirmedWorking: 'Recently confirmed working',
  health: 'Health',
  openIssues: 'Open issues',
  rating: 'Rating',
} as const;

/** Format an ISO timestamp as a local date, or an em dash when null/unparseable. */
export function shortDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

// ── Signal 1: adoption (Card: yes · Detail: yes, with absence copy) ───────────────────────────────

/** The fields signal 1 reads. Both `CatalogueToolSummary` and `CatalogueToolDetail` satisfy this. */
export interface AdoptionSignal {
  adoptionCount: number;
  lastAdoptedAt: string | null;
}

/**
 * TD-F07's absence copy for signal 1, and the one string every surface shares verbatim. A tool nobody
 * has taken yet is a normal state, not an error and not a zero — `AE-F19` puts the count on every
 * surface, so every surface also needs this sentence.
 */
const NOT_ADOPTED_YET = 'Not adopted yet';

/**
 * The count as a phrase about PEOPLE, because that is what it counts.
 *
 * `AE §11.1` defines the adoption count as DISTINCT adopters with the builder excluded, and
 * `db/adoption.ts` enforces it — so one colleague who adopts five times contributes 1 (`AE-F09`) and a
 * builder padding their own figure contributes 0 (`AE-F10`). This wording used to say "3 times", which
 * invited a reader to do the arithmetic the count deliberately refuses to do: five adoptions by one
 * person is not five times on this figure. `REQUIREMENTS_TOOL_DETAIL_PAGE.md:107` states the reading
 * the number is meant to carry — "how many people actually took it" — and that is what it now says.
 *
 * Private and shared by both forms below, so the noun cannot drift between the tool page and a board.
 */
function adopterPhrase(count: number): string {
  return `${count} colleague${count === 1 ? '' : 's'}`;
}

/**
 * The trailing window a count covers, as the words that go after "adopted". Empty for a whole-life
 * count, which is stated by saying nothing rather than by "ever" — an unqualified count reads as the
 * total, and that is what it is.
 *
 * DERIVED FROM THE NUMBER OF DAYS, never passed as prose. The two windowed surfaces get theirs from a
 * platform setting or a scoring constant, and a caller free to write its own phrase is a caller free to
 * label a 30-day figure "this week" — which is the bug this whole file exists to make impossible. Seven
 * days is spelled "this week" because that is the product's own vocabulary for it (`RT-F01` the WEEKLY
 * Top 5, `RT-F11` "new this week"); every other window is stated in days so it cannot be rounded in the
 * reader's head.
 */
function windowPhrase(windowDays: number | null | undefined): string {
  if (windowDays == null) return '';
  if (windowDays === 7) return 'this week';
  return `in the last ${windowDays} days`;
}

/**
 * §8's card/detail form: "3 colleagues (last 12 Aug 2026)", or TD-F07's absence copy.
 *
 * Never returns null: an unadopted tool is the case TD-F07 exists for — "not adopted yet" is
 * information a person can act on, and a blank where a count belongs reads as a loading failure.
 * `lastAdoptedAt` is dropped rather than em-dashed when absent, because a count above zero always has
 * a last adoption; a null there means a clock/prune oddity, not a fact worth showing.
 *
 * Whole-life by construction — `adoptionSignalsSql` takes no window — so this form carries no window
 * phrase. `formatAdopterCount` is the board form of the same number; see its note on why there are two.
 */
export function formatAdoption(t: AdoptionSignal): string {
  if (t.adoptionCount <= 0) return NOT_ADOPTED_YET;
  const who = adopterPhrase(t.adoptionCount);
  return t.lastAdoptedAt ? `${who} (last ${shortDate(t.lastAdoptedAt)})` : who;
}

/**
 * The BOARD form of signal 1: "3 colleagues adopted", "3 colleagues adopted this week", "Not adopted
 * in the last 30 days". For the Weekly Top 5 card, the four homepage boards and the Learning Hub tile
 * — the surfaces `AE-F19` adds to the two §8 governs.
 *
 * WHY A SECOND FORM AT ALL, when this file exists to stop a signal being worded twice. Two things are
 * genuinely different about a board card and cannot be reconciled by picking one string:
 *
 *   1. A board card's figure sits in a one-line meta strip beside the builder and the category, where
 *      "Adopted · 3 colleagues (last 12 Aug 2026)" does not fit and has no `<dt>` to answer. So the
 *      verb is in the sentence here and in the label there.
 *   2. Two of these surfaces count a WINDOW. Trending is a trailing horizon (`FR3`, 30 days) and the
 *      Learning Hub tile is this week's most-adopted (`decision #6`, 7 days), while Best of all time,
 *      the chronological boards and the Top 5 card are whole-life. An unlabelled window is the actual
 *      defect `AE-F19`'s audit turned up: Trending and Best of all time rendered the SAME sentence for
 *      a 30-day count and a lifetime count, so one tool appeared on one page with two different
 *      figures and nothing saying why.
 *
 * What the two forms are NOT allowed to differ on, and what this file guarantees: the noun
 * (`adopterPhrase`), the absence copy at zero (`NOT_ADOPTED_YET`), and the value — every count comes
 * from `db/adoption.ts`'s one fragment. A reader who sees "3 colleagues" on a card and "3 colleagues
 * adopted" on a board is reading one number twice, which is `AE-F19`.
 *
 * Zero is a sentence, not "0 colleagues adopted". It is reachable on every board here: a Top 5 row can
 * rank on likes and ratings alone, and the chronological boards are deliberately not adoption-gated
 * (`RT-F11` — a tool published this morning has no adopters and still belongs on "New this week").
 */
export function formatAdopterCount(count: number, windowDays?: number | null): string {
  const when = windowPhrase(windowDays);
  if (count <= 0) return when ? `Not adopted ${when}` : NOT_ADOPTED_YET;
  return when ? `${adopterPhrase(count)} adopted ${when}` : `${adopterPhrase(count)} adopted`;
}

// ── Signal 2: review marker (Card: icon only · Detail: full) ──────────────────────────────────────

export type ReviewMarkerState = 'none' | 'current' | 'lapsed';

/** The fields the DETAIL half of signal 2 reads — the date and the version reviewed. */
export interface ReviewSignal {
  reviewMarker: ReviewMarkerState;
  reviewedAt: string | null;
  reviewedVersion: number | null;
}

/**
 * The tool page's full form: "12 Aug 2026 (version 3)", "Lapsed — 12 Aug 2026 (version 2)", or TD-F07's
 * absence copy.
 *
 * A `current` marker with no `reviewedAt` is treated as unreviewed rather than dated with an em dash:
 * the marker column and the `app.tool_review` row are written by different code paths (CAT-F17 lapses
 * the marker on a material edit), and a review with no date is a bookkeeping artefact, not a review a
 * person should be told about.
 */
export function formatReviewMarker(t: ReviewSignal): string {
  if (t.reviewMarker === 'none' || !t.reviewedAt) return 'Not yet reviewed';
  const prefix = t.reviewMarker === 'lapsed' ? 'Lapsed — ' : '';
  const version = t.reviewedVersion != null ? ` (version ${t.reviewedVersion})` : '';
  return `${prefix}${shortDate(t.reviewedAt)}${version}`;
}

/** A card's icon-sized review marker: a glyph and the words a screen reader gets instead of it. */
export interface ReviewMarkerIcon {
  glyph: string;
  label: string;
}

/**
 * §8 gives a card an ICON for signal 2 and the page the full line. `null` means the card shows nothing.
 *
 * WHY `none` IS NOTHING, when TD-F07 asks for explicit absence everywhere else: the absence copy is a
 * SENTENCE ("Not yet reviewed"), and §8 allows a card only an icon. There is no glyph that reads as "no
 * review has happened here" rather than as a warning about the tool itself — and a card that warned
 * about every unreviewed tool would libel most of an open-publishing catalogue. So the sentence stays on
 * the page, where a person can read it, and the card is silent. The lapsed case is the one a card must
 * carry, because a lapsed review is a fact about a tool that WAS reviewed and no longer is.
 */
export function reviewMarkerIcon(state: ReviewMarkerState): ReviewMarkerIcon | null {
  if (state === 'current') return { glyph: '✓', label: 'Reviewed' };
  if (state === 'lapsed') return { glyph: '!', label: 'Review lapsed' };
  return null;
}

// ── Signal 3: recently confirmed working (Card: yes · Detail: yes) ────────────────────────────────

/**
 * "Yes — 7 people confirmed recently" at or above the floor, "Yes, recently" below it, `null` at zero.
 *
 * Three renderings for one number, and each one is a rule:
 *   - zero is NOT "nobody has confirmed this" — nobody being asked yet looks identical to a tool people
 *     tried and abandoned, so the signal is absent rather than negative (FB-F14);
 *   - below the floor the answer is qualitative, because "1 person confirmed recently" invites a reader
 *     to weigh a sample of one;
 *   - at or above it the number is the point.
 *
 * `floor` is a parameter so a test can pin all three branches without depending on today's default, and
 * so a future settings read has one place to feed.
 */
export function formatConfirmedWorking(
  count: number,
  floor: number = MIN_CONFIRMATIONS_TO_SHOW_COUNT,
): string | null {
  if (count <= 0) return null;
  if (count >= floor) return `Yes — ${count} people confirmed recently`;
  return 'Yes, recently';
}

// ── Signal 4: health (Card: yes if not healthy · Detail: yes) ────────────────────────────────────

/**
 * The health state verbatim, or `null` when the tool is Healthy — §8's "Card: yes (if not healthy)".
 *
 * Healthy is the majority state and saying so on every card is noise that trains people to stop reading
 * the field, which is exactly when a Broken one goes unnoticed. The state is passed through UNCHANGED:
 * `app.tool.health_state` is already the plain-words vocabulary a person reads ('Healthy', 'Broken',
 * 'Unmaintained'), and re-wording it here would be a second vocabulary to keep in step with the sweep.
 */
export function formatHealth(healthState: string): string | null {
  return healthState === 'Healthy' ? null : healthState;
}

// ── Signal 5: open issues (Card: count · Detail: the reports themselves) ─────────────────────────

/**
 * "2 open issues" for a card, `null` when there are none.
 *
 * The card gets a count and the page gets the list, per §8 — so this is the only shared wording, and
 * "no open issues" is deliberately not said: TD-F15's safety warning is what speaks up when a tool has
 * a problem, and an all-clear on every card would drown it.
 */
export function formatOpenIssueCount(count: number): string | null {
  if (count <= 0) return null;
  return `${count} open issue${count === 1 ? '' : 's'}`;
}

/**
 * The fields the tool page's signal-5 rendering reads. Satisfied by `CatalogueToolDetail`; the CARD
 * has only `openIssueCount` and needs only `formatOpenIssueCount` above, because a card never shows a
 * report's body on any surface.
 */
export interface OpenIssuesSignal {
  openIssueCount: number;
  openIssuesVisibility: 'full' | 'count_only';
  openIssuesWithheldReason: string | null;
}

/**
 * What the tool page shows for signal 5. Three cases, and the middle one is the whole point.
 *
 *   'none'      — no open reports. Say nothing (see `formatOpenIssueCount` above).
 *   'bodies'    — the viewer may read them: render the reports, exactly as this page always has.
 *   'countOnly' — there ARE open reports and this viewer may not read them: say how many, and say why.
 */
export type OpenIssuesDisplay =
  { kind: 'none' } | { kind: 'bodies' } | { kind: 'countOnly'; count: string; reason: string };

/**
 * Shown to a viewer whose payload says 'count_only' but carries no reason — which the API never sends.
 * Deliberately vaguer than the API's own sentence rather than a second copy of it: a copy would be one
 * more place for the list of privileged parties to drift out of step with the gate that enforces it.
 */
const WITHHELD_REASON_FALLBACK = 'These reports are private to the people the report form names.';

/**
 * TD-F09 vs TD-F20, decided in one place because getting it wrong is silent.
 *
 * The API gates the report BODIES but never the COUNT, so `openIssues` arrives empty for an ordinary
 * colleague while `openIssueCount` still says 2. A page that asked `openIssues.length > 0` would render
 * nothing at all — publishing "no open issues" about a tool that has two, to everyone except the
 * builder, the platform owner and the SWAT team. That is worse than the leak the gate closes: a
 * colleague would adopt believing nobody had reported a fault. This function is the reason no surface
 * has to remember that, and `test/trustSignals.test.ts` is the reason it stays true.
 */
export function openIssuesDisplay(t: OpenIssuesSignal): OpenIssuesDisplay {
  const count = formatOpenIssueCount(t.openIssueCount);
  if (count === null) return { kind: 'none' };
  if (t.openIssuesVisibility === 'full') return { kind: 'bodies' };
  return {
    kind: 'countOnly',
    count,
    reason: t.openIssuesWithheldReason ?? WITHHELD_REASON_FALLBACK,
  };
}

// ── Signal 10: rating (Card: yes · Detail: yes) ──────────────────────────────────────────────────

/** The fields signal 10 reads. Satisfied by both the summary and the detail. */
export interface RatingSignal {
  ratingAverage: number | null;
  ratingCount: number;
}

/**
 * "4.3 / 5 (7 ratings)", or "Not yet rated".
 *
 * `ratingAverage === null` is the whole contract (FB-F21): the API withholds an average below
 * `min_ratings_to_show_average`, so null means "no publishable average", which covers both a tool nobody
 * has rated and a tool two people rated 5. Neither is 0 stars, and a card that rendered `?? 0` would
 * turn the best-liked new tool on the page into the worst-rated one.
 *
 * The COUNT is deliberately not shown alongside "Not yet rated". It is true and it is unhelpful: "Not
 * yet rated (2 ratings)" is a contradiction to read, and the tool page's rate control is where a person
 * finds out their rating will count.
 */
export function formatRating(t: RatingSignal): string {
  if (t.ratingAverage == null) return 'Not yet rated';
  return `${t.ratingAverage.toFixed(1)} / 5 (${t.ratingCount} rating${t.ratingCount === 1 ? '' : 's'})`;
}
